import type { StateCreator } from 'zustand';
import type { DateRange } from 'react-day-picker';
import { setHours, setMinutes, setSeconds, addMinutes } from 'date-fns';
import type { Service } from '../../../typings/common/booking';

const DATE_ONLY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseBookingDateTime(value: string): Date | null {
	const input = value.trim();
	if (!input) {
		return null;
	}

	const dateOnlyMatch = input.match(DATE_ONLY_REGEX);
	if (dateOnlyMatch) {
		const [, year, month, day] = dateOnlyMatch;
		const parsed = new Date(
			parseInt(year, 10),
			parseInt(month, 10) - 1,
			parseInt(day, 10)
		);
		return isNaN(parsed.getTime()) ? null : parsed;
	}

	// Normalize common "Y-m-d H:i(:s)" format into an ISO-like local datetime.
	// This avoids cross-browser inconsistencies when parsing values with spaces.
	const normalized = input.includes(' ') ? input.replace(' ', 'T') : input;
	const parsed = new Date(normalized);
	return isNaN(parsed.getTime()) ? null : parsed;
}

export interface DateTimeSlice {
	// State - Range is the single source of truth
	selectedRange: DateRange | undefined;

	/**
	 * Stores the original booking range when editing an entry.
	 * This is used to display the "before editing" state and highlight
	 * the original date/time in the UI.
	 */
	originalRange: DateRange | undefined;

	/**
	 * Tracks whether the store has been initialized from the hidden input value.
	 * This prevents race conditions in multi-page forms where useBookingTimeValue
	 * would compute an empty string and overwrite the hidden input before the
	 * initialize() function could restore the saved state from the hidden input.
	 */
	isInitialized: boolean;

	// Actions
	setSelectedRange: (range: DateRange | undefined) => void;
	reset: () => void;

	// Initialization from hidden input
	initialize: (initialValue: string, service: Service) => void;

	// Convenience setters - update selectedRange
	setSelectedDate: (date: Date | undefined) => void;
	setSelectedTime: (time: string | undefined) => void;
	setSelectedDuration: (duration: number) => void;
}

export const createDateTimeSlice: StateCreator<DateTimeSlice> = (set, get) => ({
	selectedRange: undefined,
	originalRange: undefined,
	isInitialized: false,

	setSelectedRange: (range) =>
		set({ selectedRange: range, isInitialized: true }),

	reset: () =>
		set({
			selectedRange: undefined,
			originalRange: undefined,
			isInitialized: false,
		}),

	initialize: (initialValue: string, service: Service) => {
		if (!service) {
			return;
		}

		if (!initialValue) {
			set({ isInitialized: true });
			return;
		}

		// Parse the range format: "start|end" or single "datetime".
		const [startRaw, endRaw] = initialValue.includes('|')
			? initialValue.split('|', 2)
			: [initialValue, initialValue];
		const startStr = (startRaw ?? '').trim();
		const endStr = (endRaw ?? startRaw ?? '').trim();

		const from = parseBookingDateTime(startStr);
		const to = parseBookingDateTime(endStr);

		if (!from || !to) {
			// eslint-disable-next-line no-console
			console.error(
				'Failed to initialize booking state from value:',
				initialValue
			);
			set({ isInitialized: true });
			return;
		}

		set({
			selectedRange: { from, to },
			originalRange: { from, to }, // Store original for entry edit mode
			isInitialized: true,
		});
	},

	// Convenience setters - update selectedRange
	setSelectedDate: (date) => {
		const currentRange = get().selectedRange;
		if (!date) {
			set({ selectedRange: undefined, isInitialized: true });
			return;
		}

		// Preserve time from existing range, or use midnight
		let from = new Date(date);
		if (currentRange?.from) {
			from = setHours(from, currentRange.from.getHours());
			from = setMinutes(from, currentRange.from.getMinutes());
			from = setSeconds(from, 0);
		}

		// If we have an end time, preserve the duration
		// Use floor to avoid rounding up (e.g., 1439.98 min -> 1440 min would add an extra day)
		let to = from;
		if (currentRange?.to && currentRange?.from) {
			const durationMinutes = Math.floor(
				(currentRange.to.getTime() - currentRange.from.getTime()) /
					(1000 * 60)
			);
			to = addMinutes(from, durationMinutes);
		}

		set({ selectedRange: { from, to }, isInitialized: true });
	},

	setSelectedTime: (time) => {
		const currentRange = get().selectedRange;

		// If no time provided, don't clear the range - just return
		// This prevents losing the date when the user clears the time input while editing
		if (!time) {
			return;
		}

		// If no date is selected yet, we can't set a time
		// The user must select a date first
		if (!currentRange?.from) {
			return;
		}

		// Parse time and update the from date using date-fns
		const [hours, mins] = time.split(':').map(Number);
		let from = new Date(currentRange.from);
		from = setHours(from, hours);
		from = setMinutes(from, mins);
		from = setSeconds(from, 0);

		// Preserve duration if we have an end time
		// Use floor to avoid rounding up (e.g., 1439.98 min -> 1440 min would add an extra day)
		let to = from;
		if (currentRange?.to && currentRange?.from) {
			const durationMinutes = Math.floor(
				(currentRange.to.getTime() - currentRange.from.getTime()) /
					(1000 * 60)
			);
			to = addMinutes(from, durationMinutes);
		}

		set({ selectedRange: { from, to }, isInitialized: true });
	},

	setSelectedDuration: (duration) => {
		const currentRange = get().selectedRange;
		if (!currentRange?.from) {
			return;
		}

		const to = addMinutes(currentRange.from, duration);
		set({
			selectedRange: { from: currentRange.from, to },
			isInitialized: true,
		});
	},
});
